import React, { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Mail, Send, X, CheckCircle2, Loader2 } from "lucide-react";

export default function SendEmailDialog({ invoiceId, defaults = {}, onClose }) {
  const [form, setForm] = useState({
    to_email: defaults.to_email || "",
    recipient_name: defaults.recipient_name || "",
    custom_message: defaults.custom_message || "",
    include_xml: true,
  });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [sender, setSender] = useState({ name: "", email: "" });

  useEffect(() => {
    (async () => {
      try {
        const [{ data: me }, { data: profile }] = await Promise.all([
          api.get("/auth/me"),
          api.get("/profile/company").catch(() => ({ data: {} })),
        ]);
        setSender({
          name: profile?.name || me?.company_name || "FakturaFlow ZUGFeRD",
          email: profile?.email || me?.email || "",
        });
      } catch {}
    })();
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setErr("");
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/send-email`, form);
      setDone(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4" data-testid="send-email-dialog">
      <div className="card w-full max-w-xl p-6 fade-in relative">
        <button className="absolute top-3 right-3 btn-ghost p-2" onClick={onClose} data-testid="close-email-dialog"><X size={16}/></button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <Mail size={18} className="text-blue-400"/>
          </div>
          <div>
            <h3 className="font-display text-xl font-bold">Rechnung per E-Mail senden</h3>
            <p className="text-xs text-slate-400 mt-0.5">Mit PDF-Anhang (ZUGFeRD) und optional separatem XML</p>
          </div>
        </div>

        {done ? (
          <div className="text-center py-6 space-y-3" data-testid="email-sent-success">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 size={28} className="text-emerald-400"/>
            </div>
            <div className="font-display text-lg font-semibold">E-Mail versendet</div>
            <div className="text-sm text-slate-400">An: <span className="text-slate-200">{form.to_email}</span></div>
            <div className="text-xs font-mono text-slate-500">Anhänge: {done.attachments?.join(", ")}</div>
            <button className="btn-primary mt-2" onClick={onClose} data-testid="close-after-send">Schließen</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label">Empfänger-E-Mail *</label>
              <input type="email" required className="input" value={form.to_email} onChange={onChange("to_email")} placeholder="kunde@firma.de" data-testid="email-to-input"/>
            </div>
            <div>
              <label className="label">Empfänger-Name</label>
              <input className="input" value={form.recipient_name} onChange={onChange("recipient_name")} placeholder="Herr Mustermann" data-testid="email-name-input"/>
            </div>
            <div>
              <label className="label">Persönliche Nachricht (optional)</label>
              <textarea rows={4} className="textarea" value={form.custom_message} onChange={onChange("custom_message")} placeholder="Vielen Dank für Ihren Auftrag…" data-testid="email-message-input"/>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.include_xml} onChange={onChange("include_xml")} className="accent-blue-500" data-testid="email-include-xml"/>
              Separate XML-Datei als zusätzlichen Anhang hinzufügen (empfohlen)
            </label>
            {err && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3" data-testid="email-error">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={onClose} data-testid="cancel-email-button">Abbrechen</button>
              <button type="submit" className="btn-primary" disabled={sending} data-testid="send-email-submit">
                {sending ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
                {sending ? "Sende..." : "Jetzt senden"}
              </button>
            </div>
            <div className="text-[11px] text-slate-500 border-t border-slate-800 pt-3" data-testid="email-sender-hint">
              Absendername: <span className="font-mono text-slate-300">{sender.name || "—"}</span>
              {sender.email ? <> · Antworten gehen an <span className="font-mono text-slate-300">{sender.email}</span></> : null}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
